import {
  getFixtureVariance,
  getFixtureVariancePercent,
  roundFixtureValue,
  sumCompleteFixtureValues,
} from '@/features/actual-vs-budget/fixture-financials'
import {
  formatPercentage,
  formatUsdMillions,
} from '@/features/actual-vs-budget/format'
import type {
  ActualVsBudgetCompanyFixture,
  ActualVsBudgetPlDirection,
  ActualVsBudgetPlRowFixture,
  ActualVsBudgetPlRowKey,
  ActualVsBudgetPlUnit,
} from '@/features/actual-vs-budget/types'

export type PlReviewPeriod = 'current-month' | 'qtd' | 'fytd' | 'full-year'

export const plReviewPeriods = [
  { key: 'current-month', label: 'Current Month' },
  { key: 'qtd', label: 'QTD' },
  { key: 'fytd', label: 'FYTD' },
  { key: 'full-year', label: 'Full Year' },
] as const

export interface CompanyPlReviewRow {
  key: ActualVsBudgetPlRowKey
  label: string
  direction: ActualVsBudgetPlDirection
  unit: ActualVsBudgetPlUnit
  indent: ActualVsBudgetPlRowFixture['indent']
  emphasis: ActualVsBudgetPlRowFixture['emphasis']
  actual: number | null
  budget: number | null
  priorYearActual: number | null
  forecast: number | null
  varianceVsBudgetAmount: number | null
  varianceVsBudgetPercent: number | null
  varianceVsBudgetPoints: number | null
  varianceVsPriorYearPercent: number | null
  varianceVsPriorYearPoints: number | null
  forecastVsBudgetAmount: number | null
  forecastVsBudgetPercent: number | null
  forecastVsBudgetPoints: number | null
  budgetStatus: PlReviewStatus
  priorYearStatus: PlReviewStatus
  forecastStatus: PlReviewStatus
  primaryStatus: PlReviewStatus
  actualMissingReason: string | null
  budgetMissingReason: string | null
  priorYearMissingReason: string | null
  forecastMissingReason: string | null
  actualDisplay: string
  budgetDisplay: string
  priorYearActualDisplay: string
  forecastDisplay: string
  varianceVsBudgetDisplay: string
  varianceVsBudgetPercentDisplay: string
  varianceVsPriorYearDisplay: string
  forecastVsBudgetDisplay: string
}

export type PlReviewStatus =
  | 'favorable'
  | 'unfavorable'
  | 'neutral'
  | 'unavailable'

export interface CompanyPlReviewPeriod {
  key: PlReviewPeriod
  label: string
  contextLabel: string
  hasMatchedData: boolean
  rows: readonly CompanyPlReviewRow[]
}

export interface CompanyPlReview {
  periods: Record<PlReviewPeriod, CompanyPlReviewPeriod>
}

function formatMonthYear(periodEnd: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${periodEnd}T00:00:00Z`))
}

function getLatestClosedIndex(company: ActualVsBudgetCompanyFixture) {
  const firstRow = company.plRows[0]
  const latestClosedIndex = firstRow?.months.findIndex(
    ({ periodEnd }) => periodEnd === company.latestClosedPeriodEnd
  )

  if (latestClosedIndex === undefined || latestClosedIndex < 0) {
    throw new Error(
      `Missing latest closed period ${company.latestClosedPeriodEnd} for ${company.id} P&L`
    )
  }

  return latestClosedIndex
}

function getSelectedIndexes(period: PlReviewPeriod, latestClosedIndex: number) {
  if (period === 'current-month') return [latestClosedIndex]
  if (period === 'qtd') {
    const quarterStart = Math.floor(latestClosedIndex / 3) * 3
    return Array.from(
      { length: latestClosedIndex - quarterStart + 1 },
      (_, index) => quarterStart + index
    )
  }
  if (period === 'fytd') {
    return Array.from({ length: latestClosedIndex + 1 }, (_, index) => index)
  }
  return Array.from({ length: 12 }, (_, index) => index)
}

function aggregateCurrencyRow(
  row: ActualVsBudgetPlRowFixture,
  indexes: readonly number[],
  field: 'actual' | 'budget' | 'priorYearActual'
) {
  return sumCompleteFixtureValues(
    indexes.map((index) => row.months[index]?.[field] ?? null)
  )
}

function aggregateMargin(
  company: ActualVsBudgetCompanyFixture,
  indexes: readonly number[],
  field: 'actual' | 'budget' | 'priorYearActual'
) {
  const revenue = company.plRows.find(({ key }) => key === 'revenue')
  const grossProfit = company.plRows.find(({ key }) => key === 'gross-profit')

  if (!revenue || !grossProfit) return null

  const revenueTotal = aggregateCurrencyRow(revenue, indexes, field)
  const grossProfitTotal = aggregateCurrencyRow(grossProfit, indexes, field)

  if (
    revenueTotal === null ||
    revenueTotal === 0 ||
    grossProfitTotal === null
  ) {
    return null
  }

  return Math.round((grossProfitTotal / revenueTotal) * 10_000) / 10_000
}

function aggregateMatchedValue(
  company: ActualVsBudgetCompanyFixture,
  row: ActualVsBudgetPlRowFixture,
  indexes: readonly number[],
  field: 'actual' | 'budget' | 'priorYearActual'
) {
  return row.unit === 'percentage-points'
    ? aggregateMargin(company, indexes, field)
    : aggregateCurrencyRow(row, indexes, field)
}

function getPointVariance(value: number | null, comparison: number | null) {
  if (value === null || comparison === null) return null
  return roundFixtureValue(value - comparison, 4)
}

function getVarianceStatus(
  variance: number | null,
  direction: ActualVsBudgetPlDirection
): PlReviewStatus {
  if (variance === null) return 'unavailable'
  if (direction === 'neutral' || variance === 0) return 'neutral'

  const favorable =
    direction === 'higher-is-better' ? variance > 0 : variance < 0
  return favorable ? 'favorable' : 'unfavorable'
}

function formatPointVariance(value: number | null) {
  if (value === null) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)} pts`
}

function formatPlValue(value: number | null, unit: ActualVsBudgetPlUnit) {
  if (value === null) return '—'
  if (unit === 'usd-millions') return formatUsdMillions(value, '—')
  const sign = value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

function getMissingReason(value: number | null, reason: string): string | null {
  return value === null ? reason : null
}

function normalizePlValue(value: number | null, unit: ActualVsBudgetPlUnit) {
  if (value === null) return null
  return roundFixtureValue(value, unit === 'usd-millions' ? 1 : 4)
}

function buildRows(
  company: ActualVsBudgetCompanyFixture,
  period: PlReviewPeriod,
  latestClosedIndex: number
): readonly CompanyPlReviewRow[] {
  const indexes = getSelectedIndexes(period, latestClosedIndex)
  const fiscalYearIsComplete =
    latestClosedIndex === 11 &&
    company.plRows.every((row) =>
      row.months.every(({ actual }) => actual !== null)
    )

  return company.plRows.map((row) => {
    const actual =
      period === 'full-year' && !fiscalYearIsComplete
        ? null
        : aggregateMatchedValue(company, row, indexes, 'actual')
    const budget = normalizePlValue(
      period === 'full-year'
        ? row.fullYearBudget
        : aggregateMatchedValue(company, row, indexes, 'budget'),
      row.unit
    )
    const priorYearActual = normalizePlValue(
      period === 'full-year'
        ? row.priorCompletedYearActual
        : aggregateMatchedValue(company, row, indexes, 'priorYearActual'),
      row.unit
    )
    const forecast = normalizePlValue(
      period === 'full-year' ? row.fullYearForecast : null,
      row.unit
    )
    const varianceVsBudgetAmount =
      row.unit === 'usd-millions' ? getFixtureVariance(actual, budget) : null
    const varianceVsBudgetPercent =
      row.unit === 'usd-millions'
        ? getFixtureVariancePercent(actual, budget)
        : null
    const varianceVsBudgetPoints =
      row.unit === 'percentage-points' ? getPointVariance(actual, budget) : null
    const varianceVsPriorYearPercent =
      row.unit === 'usd-millions'
        ? getFixtureVariancePercent(actual, priorYearActual)
        : null
    const varianceVsPriorYearPoints =
      row.unit === 'percentage-points'
        ? getPointVariance(actual, priorYearActual)
        : null
    const forecastVsBudgetAmount =
      row.unit === 'usd-millions' ? getFixtureVariance(forecast, budget) : null
    const forecastVsBudgetPercent =
      row.unit === 'usd-millions'
        ? getFixtureVariancePercent(forecast, budget)
        : null
    const forecastVsBudgetPoints =
      row.unit === 'percentage-points'
        ? getPointVariance(forecast, budget)
        : null
    const budgetVariance =
      row.unit === 'usd-millions'
        ? varianceVsBudgetAmount
        : varianceVsBudgetPoints
    const forecastVariance =
      row.unit === 'usd-millions'
        ? forecastVsBudgetAmount
        : forecastVsBudgetPoints
    const priorYearVariance =
      row.unit === 'usd-millions'
        ? getFixtureVariance(actual, priorYearActual)
        : varianceVsPriorYearPoints
    const budgetStatus = getVarianceStatus(budgetVariance, row.direction)
    const priorYearStatus = getVarianceStatus(priorYearVariance, row.direction)
    const forecastStatus = getVarianceStatus(forecastVariance, row.direction)

    return {
      key: row.key,
      label: row.label,
      direction: row.direction,
      unit: row.unit,
      indent: row.indent,
      emphasis: row.emphasis,
      actual,
      budget,
      priorYearActual,
      forecast,
      varianceVsBudgetAmount,
      varianceVsBudgetPercent,
      varianceVsBudgetPoints,
      varianceVsPriorYearPercent,
      varianceVsPriorYearPoints,
      forecastVsBudgetAmount,
      forecastVsBudgetPercent,
      forecastVsBudgetPoints,
      budgetStatus,
      priorYearStatus,
      forecastStatus,
      primaryStatus: period === 'full-year' ? forecastStatus : budgetStatus,
      actualMissingReason:
        actual === null && period === 'full-year' && !fiscalYearIsComplete
          ? 'Fiscal year not complete'
          : getMissingReason(actual, 'Actual data unavailable for this period'),
      budgetMissingReason: getMissingReason(
        budget,
        period === 'full-year'
          ? 'Budget data unavailable'
          : 'Budget data unavailable for this period'
      ),
      priorYearMissingReason: getMissingReason(
        priorYearActual,
        'Prior-year Actual unavailable'
      ),
      forecastMissingReason:
        period === 'full-year'
          ? getMissingReason(forecast, 'Forecast unavailable')
          : null,
      actualDisplay: formatPlValue(actual, row.unit),
      budgetDisplay: formatPlValue(budget, row.unit),
      priorYearActualDisplay: formatPlValue(priorYearActual, row.unit),
      forecastDisplay: formatPlValue(forecast, row.unit),
      varianceVsBudgetDisplay:
        row.unit === 'usd-millions'
          ? formatUsdMillions(varianceVsBudgetAmount, '—')
          : formatPointVariance(varianceVsBudgetPoints),
      varianceVsBudgetPercentDisplay:
        row.unit === 'usd-millions'
          ? formatPercentage(varianceVsBudgetPercent, '—')
          : '—',
      varianceVsPriorYearDisplay:
        row.unit === 'usd-millions'
          ? formatPercentage(varianceVsPriorYearPercent, '—')
          : formatPointVariance(varianceVsPriorYearPoints),
      forecastVsBudgetDisplay:
        row.unit === 'usd-millions'
          ? `${formatUsdMillions(forecastVsBudgetAmount, '—')} · ${formatPercentage(forecastVsBudgetPercent, '—')}`
          : formatPointVariance(forecastVsBudgetPoints),
    }
  })
}

function getPeriodContext(
  company: ActualVsBudgetCompanyFixture,
  period: PlReviewPeriod,
  latestClosedIndex: number
) {
  const through = formatMonthYear(company.latestClosedPeriodEnd)

  if (period === 'current-month') return through
  if (period === 'qtd') {
    return `Q${Math.floor(latestClosedIndex / 3) + 1} through ${through}`
  }
  if (period === 'fytd') return `FYTD through ${through}`
  return company.fiscalYearLabel
}

function buildPeriod(
  company: ActualVsBudgetCompanyFixture,
  period: PlReviewPeriod,
  latestClosedIndex: number
): CompanyPlReviewPeriod {
  const definition = plReviewPeriods.find(({ key }) => key === period)

  if (!definition)
    throw new Error(`Missing P&L period definition for ${period}`)

  const rows = buildRows(company, period, latestClosedIndex)
  const hasMatchedData = rows.some((row) =>
    period === 'full-year'
      ? [row.actual, row.budget, row.priorYearActual, row.forecast].some(
          (value) => value !== null
        )
      : [row.actual, row.budget, row.priorYearActual].some(
          (value) => value !== null
        )
  )

  return {
    key: period,
    label: definition.label,
    contextLabel: getPeriodContext(company, period, latestClosedIndex),
    hasMatchedData,
    rows,
  }
}

export function buildCompanyPlReview(
  company: ActualVsBudgetCompanyFixture
): CompanyPlReview {
  const latestClosedIndex = getLatestClosedIndex(company)

  return {
    periods: {
      'current-month': buildPeriod(company, 'current-month', latestClosedIndex),
      qtd: buildPeriod(company, 'qtd', latestClosedIndex),
      fytd: buildPeriod(company, 'fytd', latestClosedIndex),
      'full-year': buildPeriod(company, 'full-year', latestClosedIndex),
    },
  }
}
